<template>
  <div class="service-list-component">
    <div class="service-list">
      <div v-if="!currentGroup" class="service-list__header">
        <p
          class="service-list-header__title"
          :style="{ color: $store.registratorCaptionColor }"
        >
          {{ 'registrator_label_choose_service' | translate }} 
        </p>
      </div>

      <div v-else class="service-list__header header-group">
        <button
          class="btn-back"
          :style="{
            color: $store.registrator_text_btn,
            backgroundColor: $store.registratorBackgroundBtnColor,
          }"
          @click="backGroup()"
        >
          <ArrowIcon class="btn-back__icon" height="32" :direction="'left'" />
          {{ 'registrator_button_back' | translate }}
        </button>
        <template v-if="showIcon">
          <img
            v-if="currentGroupIconUrl"
            class="header-group__service-icon"
            :src="currentGroupIconUrl"
            draggable="false"
          />
          <svg
            v-else
            class="service-item__icon header-group__service-icon"
            width="48"
            height="54"
            viewBox="0 0 48 54"
            :fill="$store.registratorCaptionColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.6667 11V3.00001C10.6667 2.29277 10.9476 1.61449 11.4477 1.11439C11.9478 0.614295 12.6261 0.333344 13.3333 0.333344H45.3333C46.0406 0.333344 46.7189 0.614295 47.219 1.11439C47.7191 1.61449 48 2.29277 48 3.00001V40.3333C48 41.0406 47.7191 41.7189 47.219 42.219C46.7189 42.7191 46.0406 43 45.3333 43H37.3333V51C37.3333 52.472 36.1333 53.6667 34.648 53.6667H2.68533C2.33367 53.6688 1.98506 53.6014 1.65953 53.4683C1.33399 53.3353 1.03796 53.1393 0.788423 52.8915C0.53889 52.6437 0.340781 52.349 0.205478 52.0244C0.0701749 51.6998 0.000344998 51.3517 0 51L0.00800006 13.6667C0.00800006 12.1947 1.208 11 2.69067 11H10.6667ZM5.33867 16.3333L5.33333 48.3333H32V16.3333H5.33867ZM16 11H37.3333V37.6667H42.6667V5.66668H16V11ZM10.6667 24.3333H26.6667V29.6667H10.6667V24.3333ZM10.6667 35H26.6667V40.3333H10.6667V35Z"
            />
          </svg>
        </template>
        <p
          ref="headerGroupTitle"
          class="header-group__title"
          :style="{
            color: $store.registratorCaptionColor,
            fontSize: `${headerGroupTitleFontSize}`,
          }"
        >
          {{ currentGroupName || currentGroupNameFallbackTranslation }}
        </p>
      </div>

      <div
        class="service-list__services"
        :class="{ 'service-list-services__thin': servicesToShow.length < 4 }"
        :style="{ color: $store.registratorTextBtn, gap: $store.isDeviceMini ? '15px': '0' }"
      >
        <div
          v-for="serviceItem in servicesToShow"
          :key="serviceItem.id"
          class="service-list__item"
          :class="{ 'service-list__item_disabled': serviceItem.isDisabled }"
          :style="{ backgroundColor: $store.registratorBackgroundBtnColor }"
        >
          <template v-if="serviceItem.group">
            <ServiceGroup
              :item="serviceItem"
              :show-icon="showIcon"
              @click="handleGroupClick(serviceItem)"
            />
          </template>

          <template v-else>
            <ServiceItem
              :item="serviceItem"
              :show-icon="showIcon"
              @click="handleServiceClick(serviceItem)"
            />
          </template>
        </div>

        <div class="booking-code" v-if="showPreRecordButton && !currentGroup">
          <button
            class="booking-btn"
            @click="showPreRecordModal"
            :style="{
              color: $store.registrator_text_btn,
              backgroundColor: $store.registratorBackgroundBtnColor,
            }"
          >
          {{ 'on_record'  | translate }}
          </button>
        </div>
      </div>
      <template />
    </div>

    <div class="pagination-wrapper">
      <Pagination
        v-show="pagesCount > 1"
        v-model="currentPage"
        class="pagination"
        :pages-count="pagesCount"
        @changePage="currentPage = $event"
      />
    </div>
  </div>
</template>

<script>
import Pagination from './Pagination';
import ServiceItem from './ServiceItem';
import ServiceGroup from './ServiceGroup';
import ArrowIcon from './Icons/Arrow';
import { getHeightRatioParentToChild } from '@/helpers/utils';

export default {
  name: 'ServiceList',
  components: {
    ArrowIcon,
    Pagination,
    ServiceItem,
    ServiceGroup,
  },
  props: {
    services: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      groupPath: [],
      currentPage: 1,
      headerGroupTitleFontSize: 'max(2.34375vw, 30px)',
    };
  },
  computed: {
    servicesPerPage() {
      if(this.$store.isDeviceMobile) {
        return 4;
      }
      if(this.$store.isDeviceMini) {
        return 5;
      }
      if(this.$store.isDeviceMax) {
        return 8;
      }
      return 10;
      // return this.$store.isDeviceMini ? 5 : 6;
    },
    currentGroup() {
      return this.groupPath.length
        ? this.groupPath[this.groupPath.length - 1].group
        : null;
    },
    currentGroupIconUrl() {
      const groupName = this.$store.groupNames[
        this.currentGroup.group.group_names_id
      ];
      return groupName ? groupName.file.file_url : '';
    },
    currentGroupName() {
      return this.$store.groupNames[this.currentGroup.group.group_names_id]
        .name[this.$store.currentLanguage.tag];
    },
    currentGroupNameFallbackTranslation() {
      return this.$store.groupNames[this.currentGroup.group.group_names_id]
        .fallbackTranslationName;
    },
    currentGroupServices() {
      if (this.currentGroup && !this.currentGroup.child) return [];

      const root = this.currentGroup ? this.currentGroup.child : this.services;

      return root.filter((child) => {
        return child.group
          ? child.isGroupHasChildWithNumbers
          : child.isHasNumbers;
      });
    },
    pagesCount() {
      return Math.ceil(this.currentGroupServices.length / this.servicesPerPage);
    },
    servicesToShow() {
      return this.currentGroupServices.slice(
        (this.currentPage - 1) * this.servicesPerPage,
        this.currentPage * this.servicesPerPage,
      );
    },
    showIcon() {
      return Boolean(this.$store.deviceStartData.unit_settings.show_icon);
    },
    showPreRecordButton() {
      return Boolean(this.$store.deviceStartData.unit_settings.use_pre_record);
    },
  },
  watch: {
    currentGroup() {
      this.$emit('changeCurrentGroup', this.currentGroup);
      if (this.currentGroup) this.$nextTick(() => this.fitHeaderGroupTitle());
    },
  },
  created() {
    this.$root.$on('backToMainScreen', () => this.backToMainScreen());
  },
  methods: {
    handleServiceClick(service) {
      if (service.isDisabled) {
        this.$root.$emit('showModal', {
          message: this.$languageService.translateFilter(
            'registrator_service_not_available_to_call',
          ),
        });
      } else {
        this.pickTicket(service);
      }
    },
    pickTicket(ticket) {
      this.$emit('pickTicket', ticket);
    },
    handleGroupClick(group) {
      if (group.isDisabled) {
        this.$root.$emit('showModal', {
          message: this.$languageService.translateFilter(
            'registrator_service_group_not_available_call',
          ),
        });
      } else {
        this.openGroup(group);
      }
    },
    openGroup(group) {
      this.groupPath.push({
        group,
        page: this.currentPage,
      });

      this.currentPage = 1;
    },
    backToMainScreen() {
      this.$emit('clearTicket', null);
      this.groupPath = [];
      this.currentPage = 1;
    },
    backGroup() {
      const deletedGroup = this.groupPath.pop();
      this.currentPage = deletedGroup.page;
    },
    fitHeaderGroupTitle() {
      this.$nextTick(() => {
        let ratio;

        while (
          (ratio = getHeightRatioParentToChild(
            this.$refs['headerGroupTitle'],
          )) < 1
        ) {
          this.fit(ratio);
        }
      });
    },
    fit(ratio) {
      const titleFontSize = parseInt(
        getComputedStyle(this.$refs['headerGroupTitle']).getPropertyValue(
          'font-size',
        ),
      );

      if (ratio < 1) {
        this.$refs['headerGroupTitle'].style.fontSize =
          titleFontSize - 1 + 'px';
      }
    },
    showPreRecordModal() {
      this.$emit('showPreRecordModal', true);
    }
  },
};
</script>

<style lang="scss" scoped>
.device_mini {
  .service-list {
    &__item {
      width: 100%;
      height: calc(100% / 7 - min(1.25vh, 16px));
      margin: 0 0 min(1.25vh, 16px) 0;
    }
  }
}

.service-list-component {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.service-list {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  flex-grow: 1;
  align-content: baseline;

  &__header {
    width: 100%;
    display: flex;
    align-items: center;

    &.header-group {
      height: min(14.0625vh, 144px);
      max-height: min(14.0625vh, 144px);
      margin-bottom: min(3.125vh, 32px);
    }
  }

  &__services {
    max-width: 100vw;
    display: flex;
    flex-wrap: wrap;
    flex-grow: 1;
    align-content: center;
    align-items: center;
  }

  &-header__title {
    width: 100%;
    font-weight: 600;
    font-size: 2.9296875vh;
    //font-size: min(2.9296875vh, 30px);
    text-align: center;
    line-height: 1.2;
    margin: 2.34375vh;
    //margin: min(2.34375vh, 24px) 0;
  }

  &__item {
    width: calc(50% - 0.78125vh * 2);
    height: 12.5vh;
    min-height: 70px;
    margin: 0.68125vh;
    padding-left: 1.875vw;
    padding-right: 8px;
    display: flex;
    align-items: center;
    border-radius: 6px;
    box-shadow: 0px 33px 38px rgba(0, 0, 0, 0.0314074),
      0px 12px 12px rgba(0, 0, 0, 0.05), 0px 3px 3px rgba(0, 0, 0, 0.08);

    &_disabled {
      opacity: 0.6;
    }
  }
}

.service-list-services {
  &__thin {
    flex-direction: column;
    justify-content: center;
  }
}

.header-group {
  &__service-icon {
    width: max(2.8125vw, 36px);
    min-width: max(2.8125vw, 36px);
    margin-left: max(2.34375vw, 30px);
  }

  &__title {
    margin-left: max(1.71875vw, 22px);
    width: 100%;
    word-break: break-word;
  }
}

.booking-code {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 10px;
  
}

.booking-btn {
  width: 304px;
  height: 80px;
  border: none;
  border-radius: 20px;
  font-size: max(2.34375vw, 30px);
  text-transform: uppercase;
}

.btn-back {
  width: auto;
  height: auto;
  display: flex;
  align-items: center;
  border: none;
  outline: none;
  padding-top: max(1.71875vw, 22px);
  padding-bottom: max(1.71875vw, 22px);
  padding-left: max(1.25vw, 16px);
  padding-right: max(1.875vw, 24px);
  font-weight: 600;
  font-size: max(2.34375vw, 30px);
  border-radius: 20px;
  // border-radius: max(3.125vw, 40px);
  text-transform: uppercase;
  box-shadow: 0px 33px 38px rgba(0, 0, 0, 0.0314074),
    0px 12px 12px rgba(0, 0, 0, 0.05), 0px 3px 3px rgba(0, 0, 0, 0.08);

  &__icon {
    width: max(2.5vw, 32px);
    margin-right: max(1.25vw, 16px);
  }
}

.pagination-wrapper {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  min-height: 88px;
  padding-bottom: min(0.78125vh, 8px);
}
@media screen and (min-width: 1285px) {
  .service-list {
    &__item {
      padding-left: 24px;
      margin: 8px;
    }
  }
}
@media screen and(min-height: 1024px) {
  .service-list-header {
    &__title {
      font-size: 30px;
      margin: 24px;
    }
  }
}
@media screen and(max-width: 645px) {
  .booking-btn {
    height: 65px;
    width: 250px;
    font-size: 20px;
  }
  .btn-back {
    width: 155px;
    height: 60px;
    font-size: 22px;
    &__icon {
      width: 20px;
    }
  }
  .service-list {
    &__item {
      min-height: 80px;
    }
  }
}
</style>
